import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Eye, Send, X, ShoppingBag, Heart, Sparkles, ChefHat } from "lucide-react";
import SmartImage from "../components/SmartImage";
import TipDialog from "../components/TipDialog";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

function Heartburst({ id }) {
  return (
    <motion.div
      key={id}
      initial={{ y: 0, opacity: 1, scale: 0.8 }}
      animate={{ y: -200, opacity: 0, scale: 1.4 }}
      transition={{ duration: 1.6, ease: "easeOut" }}
      className="absolute right-6 bottom-32 pointer-events-none"
    >
      <Heart className="w-7 h-7 text-pink-400 fill-pink-400 drop-shadow-[0_0_8px_rgba(255,46,147,0.9)]" />
    </motion.div>
  );
}

export default function WatchLive() {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem } = useCart();
  const [stream, setStream] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [hearts, setHearts] = useState([]);
  const [showProducts, setShowProducts] = useState(false);
  const chatScrollRef = useRef(null);
  const lastTsRef = useRef(null);

  // Initial load + join
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/streams/${streamId}`);
        if (!active) return;
        setStream(data);
        await api.post(`/streams/${streamId}/join`);
      } catch {
        toast.error("Stream not found");
        navigate("/feed");
      }
    })();
    return () => { active = false; };
  }, [streamId, navigate]);

  // Poll messages + viewer count
  useEffect(() => {
    if (!streamId) return;
    const tick = async () => {
      try {
        const params = lastTsRef.current ? { after: lastTsRef.current } : {};
        const { data: newMsgs } = await api.get(`/streams/${streamId}/messages`, { params });
        if (newMsgs.length > 0) {
          setMessages((m) => [...m, ...newMsgs]);
          lastTsRef.current = newMsgs[newMsgs.length - 1].created_at;
        }
        const { data: s } = await api.get(`/streams/${streamId}`);
        setStream((prev) => ({ ...(prev || {}), ...s }));
        if (s.status !== "live") {
          // Stream ended
        }
      } catch { /* ignore */ }
    };
    tick();
    const i = setInterval(tick, 3000);
    return () => clearInterval(i);
  }, [streamId]);

  useEffect(() => {
    chatScrollRef.current?.scrollTo({ top: chatScrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    if (!user) { toast.error("Log in to chat"); return; }
    setText("");
    try {
      await api.post(`/streams/${streamId}/messages`, { text: t });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send");
    }
  };

  const tossHeart = () => {
    setHearts((h) => [...h.slice(-12), Date.now() + Math.random()]);
  };

  const onAdd = async (p) => {
    try {
      const res = await addItem(p.product_id, 1);
      if (res?.needs_customization) {
        toast.message(`Customize ${p.name}`);
      } else {
        toast.success(`${p.name} added to bag`);
      }
    } catch {
      toast.error("Couldn't add to bag");
    }
  };

  if (!stream) {
    return (
      <div className="min-h-[100dvh] grid place-items-center bg-black">
        <div className="w-10 h-10 rounded-full border-2 border-pink-500/40 border-t-pink-500 animate-spin" />
      </div>
    );
  }

  const ended = stream.status !== "live";
  const featured = stream.featured_product;
  const heroImg = featured?.image_paths?.[0] || stream.vendor_avatar;

  return (
    <div className="relative w-full h-[100dvh] bg-black overflow-hidden" data-testid="watchlive-page">
      {/* Mock broadcast: animated gradient + product hero behind */}
      <div className="absolute inset-0">
        {heroImg ? (
          <img src={fileUrl(heroImg)} alt="" className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-70" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-pink-900 via-rose-700 to-cyan-700" />
        )}
        <motion.div
          className="absolute inset-0"
          animate={{
            background: [
              "radial-gradient(circle at 30% 30%, rgba(255,46,147,0.55), transparent 50%), radial-gradient(circle at 70% 70%, rgba(0,229,255,0.45), transparent 50%)",
              "radial-gradient(circle at 70% 30%, rgba(255,46,147,0.55), transparent 50%), radial-gradient(circle at 30% 70%, rgba(0,229,255,0.45), transparent 50%)",
              "radial-gradient(circle at 30% 30%, rgba(255,46,147,0.55), transparent 50%), radial-gradient(circle at 70% 70%, rgba(0,229,255,0.45), transparent 50%)",
            ],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        {featured && (
          <div className="absolute inset-0 grid place-items-center">
            <motion.img
              key={featured.product_id}
              src={fileUrl(featured.image_paths?.[0])}
              alt={featured.name}
              className="w-72 h-72 sm:w-96 sm:h-96 object-cover rounded-3xl shadow-2xl shadow-pink-500/40"
              animate={{ scale: [1, 1.04, 1], rotate: [-1, 1, -1] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        )}
      </div>
      <div className="absolute inset-0 bg-black/30" />
      <div className="absolute inset-0 feed-overlay" />

      {/* Top status bar */}
      <div className="absolute top-0 left-0 right-0 z-20 px-4 pt-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/vendor/${stream.vendor_id}`)}
            className="flex items-center gap-2 px-2 pr-3 py-1 rounded-full glass-strong"
            data-testid="watchlive-vendor-chip"
          >
            <span className="w-7 h-7 rounded-full bg-pink-500/30 grid place-items-center overflow-hidden">
              {stream.vendor_avatar
                ? <img src={fileUrl(stream.vendor_avatar)} alt="" className="w-full h-full object-cover" />
                : <Sparkles className="w-3.5 h-3.5 text-pink-200" />
              }
            </span>
            <span className="text-xs font-bold">@{stream.vendor_name}</span>
          </button>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black tracking-widest ${
            ended ? "bg-zinc-700 text-zinc-300" : "bg-red-500 text-white pulse-glow"
          }`} data-testid="watchlive-live-badge">
            {!ended && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
            {ended ? "ENDED" : "LIVE"}
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full glass text-[11px] font-bold" data-testid="watchlive-viewers">
            <Eye className="w-3 h-3" /> {stream.viewers || 0}
          </span>
        </div>
        <button
          onClick={() => navigate("/feed")}
          className="w-9 h-9 grid place-items-center rounded-full glass-strong"
          aria-label="Close"
          data-testid="watchlive-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Title */}
      <div className="absolute top-16 left-4 right-20 z-10">
        <h1 className="text-2xl font-black tracking-tighter drop-shadow-lg" data-testid="watchlive-title">
          {stream.title}
        </h1>
      </div>

      {/* Right action sidebar */}
      <div className="absolute right-3 bottom-44 z-10 flex flex-col items-center gap-4">
        <button
          onClick={tossHeart}
          className="w-12 h-12 grid place-items-center rounded-full bg-black/60 backdrop-blur border border-white/15 hover:bg-pink-500 transition-colors"
          aria-label="Send heart"
          data-testid="watchlive-heart-btn"
        >
          <Heart className="w-5 h-5" />
        </button>
        <button
          onClick={() => setShowProducts(true)}
          className="w-12 h-12 grid place-items-center rounded-full bg-pink-500 hover:bg-pink-400 glow-pink"
          aria-label="Shop"
          data-testid="watchlive-shop-btn"
        >
          <ShoppingBag className="w-5 h-5" />
        </button>
        <TipDialog vendor={{ user_id: stream.vendor_id, name: stream.vendor_name, vendor_profile: { business_name: stream.vendor_name, payment_handles: stream.vendor_payment_handles || {} } }}
          source="livestream"
          trigger={
            <button
              className="w-12 h-12 grid place-items-center rounded-full bg-black/60 backdrop-blur border border-white/15 hover:bg-white/10"
              aria-label="Tip vendor"
              data-testid="watchlive-tip-btn"
              type="button"
            >
              <Sparkles className="w-5 h-5 text-pink-300" />
            </button>
          }
        />
      </div>

      {/* Heart bursts */}
      <AnimatePresence>
        {hearts.map((h) => <Heartburst key={h} id={h} />)}
      </AnimatePresence>

      {/* Chat overlay */}
      <div className="absolute bottom-24 left-3 right-20 z-10" data-testid="watchlive-chat">
        <div ref={chatScrollRef} className="max-h-56 overflow-y-auto no-scrollbar space-y-1.5 mb-2 pr-2">
          {messages.slice(-30).map((m) => (
            <div
              key={m.message_id}
              className="text-sm leading-tight"
              data-testid={`watchlive-msg-${m.message_id}`}
            >
              <span className={`text-[11px] font-bold mr-1.5 inline-flex items-center gap-1 ${m.is_vendor ? "text-pink-300" : "text-cyan-300"}`}>
                {m.sender_name}{m.is_vendor && <ChefHat className="w-3 h-3" />}:
              </span>
              <span className="text-white drop-shadow">{m.text}</span>
            </div>
          ))}
        </div>

        <form onSubmit={send} className="flex items-center gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={user ? "Say something…" : "Log in to chat"}
            disabled={!user || ended}
            className="flex-1 h-10 px-4 rounded-full bg-black/60 backdrop-blur border border-white/15 text-white text-sm placeholder:text-zinc-400 outline-none focus:border-pink-400"
            data-testid="watchlive-chat-input"
          />
          <button
            type="submit"
            disabled={!user || ended || !text.trim()}
            className="h-10 w-10 grid place-items-center rounded-full bg-pink-500 disabled:opacity-40"
            aria-label="Send chat"
            data-testid="watchlive-chat-send-btn"
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </form>
      </div>

      {/* Bottom shop drawer */}
      {showProducts && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          className="absolute bottom-0 left-0 right-0 z-30 glass-strong rounded-t-3xl px-4 pt-4 pb-6 max-h-[60vh] overflow-y-auto"
          data-testid="watchlive-shop-drawer"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-black tracking-tight">Shop the live</h3>
            <button onClick={() => setShowProducts(false)} className="w-8 h-8 grid place-items-center rounded-full bg-white/10" aria-label="Close shop">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {(stream.vendor_products || []).map((p) => (
              <div key={p.product_id} className="rounded-2xl overflow-hidden bg-zinc-900/80 border border-white/10" data-testid={`watchlive-product-${p.product_id}`}>
                <div className="aspect-square bg-zinc-800">
                  {p.image_paths?.[0] && (
                    <SmartImage src={fileUrl(p.image_paths[0])} alt={p.name} mode="smart_auto_fit" className="w-full h-full" testid={`watchlive-product-image-${p.product_id}`} />
                  )}
                </div>
                <div className="p-2.5">
                  <p className="font-bold text-sm truncate">{p.name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-pink-300 font-black">${p.price.toFixed(2)}</span>
                    <button
                      onClick={() => onAdd(p)}
                      className="h-8 px-3 rounded-full bg-pink-500 text-white text-xs font-bold"
                      data-testid={`watchlive-add-${p.product_id}`}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {(!stream.vendor_products || stream.vendor_products.length === 0) && (
              <p className="col-span-2 text-center text-zinc-500 py-8">No products yet.</p>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}
